// ─────────────────────────────────────────────────────────────────────────────
// Contrato do ARRANQUE — GET /loading/context.
//
// O ecrã de arranque não é decoração: é a primeira coisa que qualquer pessoa vê
// do produto, e é o único momento em que a app pode dizer, com números reais, que
// está viva. A API já publicava estes passos («SERVIDOR · CONECTADO (68
// utilizadores)», «EVENTOS · 24 DISPONÍVEIS») e nenhum dos dois frontends os
// consumia — um backend inteiro a falar sozinho.
//
// Mesma convenção do hexis-client: aqui vivem o CAMINHO, os tipos e o
// normalizador; o fetch (com a base e a autenticação de cada app) fica na app.
// ─────────────────────────────────────────────────────────────────────────────

export const LOADING_CONTEXT_PATH = '/loading/context';

/** Que história o arranque conta. `boot` é o splash das duas apps. */
export type LoadingContextKind = 'boot' | 'events' | 'auth' | 'sync' | 'system';

export interface LoadingStep {
  id: string;
  label: string;
  /** `undefined` = passo sem estado (uma etapa narrada, não verificada). */
  done?: boolean;
}

export interface LoadingContext {
  context: string;
  steps: LoadingStep[];
  /** Só o contexto `boot` o declara: o backend respondeu e está utilizável. */
  ready: boolean;
}

export function loadingContextPath(kind: LoadingContextKind = 'boot'): string {
  return `${LOADING_CONTEXT_PATH}?context=${kind}`;
}

/**
 * Normaliza a resposta. Um ecrã de arranque NUNCA pode rebentar por causa da
 * forma de um payload — se o servidor responder algo inesperado, o resultado é
 * «sem passos», e a app mostra a legenda estática de sempre. Degradar em
 * silêncio é o comportamento certo aqui, e só aqui.
 */
export function normalizeLoadingContext(raw: unknown): LoadingContext {
  const o = (raw ?? {}) as Record<string, unknown>;
  const steps = Array.isArray(o.steps) ? o.steps : [];
  return {
    context: typeof o.context === 'string' ? o.context : 'boot',
    ready: o.ready === true,
    steps: steps
      .map((s): LoadingStep | null => {
        const step = (s ?? {}) as Record<string, unknown>;
        if (typeof step.label !== 'string' || !step.label) return null;
        return {
          id: typeof step.id === 'string' ? step.id : step.label,
          label: step.label,
          done: typeof step.done === 'boolean' ? step.done : undefined,
        };
      })
      .filter((s): s is LoadingStep => s !== null),
  };
}
